import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConfirmRegistrationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, already_confirmed, error
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const hasConfirmedRef = useRef(false);

  useEffect(() => {
    // Verhindert doppelten Aufruf (React StrictMode, Race Conditions)
    if (hasConfirmedRef.current) return;
    hasConfirmedRef.current = true;
    
    const confirmRegistration = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Kein Bestätigungstoken gefunden');
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/membership/confirm/${token}`);
        
        if (response.data.success) {
          if (response.data.alreadyConfirmed) {
            setStatus('already_confirmed');
            setMessage('Diese Registrierung wurde bereits bestätigt.');
          } else {
            setStatus('success');
            setMessage(response.data.message);
            setUserData({
              name: response.data.name,
              email: response.data.email
            });
          }
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Ein Fehler ist aufgetreten');
      }
    };

    confirmRegistration();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-orange-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl bg-gradient-to-r from-orange-600 to-blue-600 bg-clip-text text-transparent">
            🐾 Dom's Pfotenflatrate
          </CardTitle>
        </CardHeader>
        
        <CardContent className="p-6">
          {status === 'loading' && (
            <div className="text-center py-12">
              <Loader2 className="h-16 w-16 mx-auto text-blue-500 animate-spin mb-4" />
              <p className="text-gray-600">Vertrag wird bestätigt...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
              
              <div>
                <h2 className="text-xl font-bold text-green-600 mb-2">
                  Vertrag erfolgreich bestätigt!
                </h2>
                <p className="text-gray-600">
                  {userData?.name && `Hallo ${userData.name}, `}
                  Ihr Vertrag ist jetzt aktiv.
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-left">
                <h3 className="font-semibold text-green-800 mb-3">✅ Was passiert als nächstes?</h3>
                <ol className="text-sm text-green-700 space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="bg-green-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                    <span>Sie erhalten eine E-Mail mit den <strong>Zahlungsinformationen</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-green-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                    <span>Überweisen Sie den Betrag mit Ihrem Namen als Verwendungszweck</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="bg-green-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                    <span>Nach Zahlungseingang erhalten Sie Ihre <strong>Mitgliedsnummer</strong></span>
                  </li>
                </ol>
              </div>

              <Button onClick={() => navigate('/')} className="w-full">
                Zur Startseite <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {status === 'already_confirmed' && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-blue-500" />
              </div>
              
              <div>
                <h2 className="text-xl font-bold text-blue-600 mb-2">
                  Bereits bestätigt
                </h2>
                <p className="text-gray-600">
                  {message}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  Wenn Sie bereits die Zahlungsinformationen erhalten haben, prüfen Sie bitte Ihren E-Mail-Posteingang.
                </p>
              </div>

              <Button onClick={() => navigate('/')} className="w-full">
                Zur Startseite <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-12 w-12 text-red-500" />
              </div>
              
              <div>
                <h2 className="text-xl font-bold text-red-600 mb-2">
                  Fehler bei der Bestätigung
                </h2>
                <p className="text-gray-600">
                  {message}
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">
                  Möglicherweise ist der Bestätigungslink abgelaufen oder ungültig. 
                  Bitte registrieren Sie sich erneut oder kontaktieren Sie uns.
                </p>
              </div>

              <div className="space-y-3">
                <Button onClick={() => navigate('/#flatrate')} className="w-full">
                  Zur Pfotenflatrate <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <p className="text-sm text-gray-500">
                  Hilfe benötigt? <a href="mailto:doms.pfotenexpress@gmail.com" className="text-blue-600 underline">doms.pfotenexpress@gmail.com</a>
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmRegistrationPage;
